import { useMemo } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight, Calendar, Clock, ChevronLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { POSTS, type PostMeta as Post } from "@/content/posts";
import { SITE } from "@/content/site";
import { buildSeo } from "@/lib/seo";
import { isPairedCategory } from "@/content/language-pairs";
import { categoryToSlug } from "@/content/blog-categories";


/** Build a lookup of slug -> canonical category name from POSTS. */
function getCategoryMap(): Map<string, { name: string; posts: Post[] }> {
  const map = new Map<string, { name: string; posts: Post[] }>();
  for (const p of POSTS) {
    const name = (p.category || "").trim();
    if (!name || name.length > 40) continue;
    const slug = categoryToSlug(name);
    if (!slug) continue;
    const entry = map.get(slug) ?? { name, posts: [] };
    entry.posts.push(p);
    map.set(slug, entry);
  }
  for (const v of map.values()) {
    v.posts.sort((a, b) => (a.date < b.date ? 1 : -1));
  }
  return map;
}

const CATEGORY_COPY: Record<string, { tagline: string; intro: string; keywords: string }> = {
  aeo: {
    tagline: "Answer Engine Optimization",
    intro:
      "Field notes on getting cited by ChatGPT, Perplexity, Google AI Overviews, and Gemini — schema, quotable answer blocks, and conversational query mapping.",
    keywords: "AEO guide, answer engine optimization, ChatGPT citations, AI Overviews, Perplexity SEO",
  },
  "technical-seo": {
    tagline: "Technical SEO",
    intro:
      "Crawl budget, Core Web Vitals, JavaScript rendering, log files, hreflang, and the rest of the plumbing that decides whether your content ever ranks.",
    keywords: "technical SEO articles, Core Web Vitals 2026, crawl budget, hreflang, JavaScript SEO",
  },
  "local-seo": {
    tagline: "Local SEO",
    intro:
      "Google Business Profile, multi-location pages, local citations, and the area-page playbooks I use for clients across Bangladesh and beyond.",
    keywords: "local SEO Bangladesh, Google Business Profile, GBP optimization, local citations",
  },
  "seo-strategy": {
    tagline: "SEO Strategy",
    intro:
      "How to plan, prioritize, and ship SEO work that actually moves revenue — topical authority, content models, and the meetings that make it happen.",
    keywords: "SEO strategy, topical authority, content strategy, SEO planning",
  },
  "industry-seo": {
    tagline: "Industry SEO",
    intro:
      "Vertical playbooks for SaaS, e-commerce, B2B, healthcare, real estate, and the sector-specific quirks that change how SEO works.",
    keywords: "SaaS SEO, ecommerce SEO, B2B SEO, vertical SEO playbooks",
  },
  "case-study": {
    tagline: "Case Studies",
    intro:
      "Receipts. Real client engagements with the strategy, the spreadsheet, and the screenshots — what worked, what didn't, and what I'd do differently.",
    keywords: "SEO case studies, AEO case study, client results, SEO portfolio",
  },
  "original-research": {
    tagline: "Original Research",
    intro:
      "Data studies and primary research on AI citations, SERP behaviour, and Bangladesh search trends — the kind of work that earns links instead of asking for them.",
    keywords: "SEO research, AI citation study, SERP study, Bangladesh search data",
  },
  tools: {
    tagline: "Tools",
    intro:
      "Walkthroughs of the free SEO tools I built — schema generator, robots tester, SERP simulator — plus reviews of the paid stack worth your money.",
    keywords: "free SEO tools, schema generator, robots tester, SERP simulator",
  },
};

function copyFor(slug: string, name: string) {
  const fallback = {
    tagline: name,
    intro: `Every article filed under ${name} on the Freelancer Tamal field journal — written from live client work, no AI slop.`,
    keywords: `${name} SEO, ${name} guide, ${name} tutorials`,
  };
  return CATEGORY_COPY[slug] ?? fallback;
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export const Route = createFileRoute("/blog/category/$category")({
  loader: ({ params }) => {
    const map = getCategoryMap();
    const entry = map.get(params.category.toLowerCase());
    if (!entry) throw notFound();
    return { name: entry.name, slug: params.category.toLowerCase(), count: entry.posts.length };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return buildSeo({
        title: "Category not found — Freelancer Tamal Blog",
        description: "This blog category does not exist.",
        path: "/blog",
        noindex: true,
      });
    }
    const copy = copyFor(loaderData.slug, loaderData.name);
    return buildSeo({
      title: `${loaderData.name} — ${loaderData.count} field notes | Freelancer Tamal`,
      description: copy.intro.slice(0, 158),
      path: `/blog/category/${loaderData.slug}`,
      ogEyebrow: copy.tagline,
      ogTitle: `${loaderData.name} field notes`,
      keywords: copy.keywords,
      ...(isPairedCategory(loaderData.slug)
        ? { alternates: { bn: `/bn/blog/category/${loaderData.slug}` } }
        : {}),
    });
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { category: slugParam } = Route.useParams();
  const slug = slugParam.toLowerCase();
  const map = useMemo(() => getCategoryMap(), []);
  const entry = map.get(slug);

  if (!entry) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="font-display text-4xl">Category not found</h1>
        <p className="mt-4 text-muted-foreground">
          That category doesn't exist (yet).{" "}
          <Link to="/blog" className="text-primary underline-offset-4 hover:underline">
            Browse all articles
          </Link>
          .
        </p>
      </div>
    );
  }

  const copy = copyFor(slug, entry.name);
  const otherCategories = Array.from(map.entries())
    .filter(([s]) => s !== slug)
    .sort((a, b) => b[1].posts.length - a[1].posts.length);

  // Schema: CollectionPage + ItemList of posts in the category.
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${entry.name} — Freelancer Tamal Blog`,
    description: copy.intro,
    url: `${SITE.url}/blog/category/${slug}`,
    isPartOf: {
      "@type": "Blog",
      name: "Freelancer Tamal — SEO Blog",
      url: `${SITE.url}/blog`,
    },
    about: { "@type": "Thing", name: entry.name },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: entry.posts.length,
      itemListElement: entry.posts.slice(0, 30).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE.url}/blog/${p.slug}`,
        name: p.title,
      })),
    },
  };

  return (
    <>
      <JsonLd data={collectionSchema} />
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: entry.name, path: `/blog/category/${slug}` },
        ]}
      />

      {/* Hero */}
      <header className="container-x pt-12 pb-16 lg:pt-20 lg:pb-24">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition mb-6"
        >
          <ChevronLeft size={12} /> All articles
        </Link>
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9">
            <div className="text-xs uppercase tracking-[0.22em] text-primary mb-5">
              {copy.tagline} · {entry.posts.length} {entry.posts.length === 1 ? "article" : "articles"}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              {entry.name}{" "}
              <span className="text-gradient">field notes.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">{copy.intro}</p>
          </div>
        </div>
      </header>

      {/* Grid */}
      <div className="container-x pb-24">
        <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {entry.posts.map((p, i) => (
            <li key={p.slug}>
              <Reveal delay={(i % 3) * 0.05}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group relative flex h-full flex-col rounded-3xl border border-border bg-card/40 p-8 hover:bg-card hover:border-primary/40 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em]">
                    <span className="text-primary">{p.category}</span>
                    <span className="text-muted-foreground inline-flex items-center gap-1.5">
                      <Clock size={11} /> {p.readMin} min
                    </span>
                  </div>
                  <h2 className="mt-5 font-display text-2xl leading-snug group-hover:text-gradient">
                    {p.title}
                  </h2>
                  <p className="mt-4 text-sm text-muted-foreground line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="mt-8 flex items-center justify-between text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={11} /> {formatDate(p.date)}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition"
                    />
                  </div>
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>

      {/* Other categories */}
      <section className="container-x pb-32">
        <div className="rounded-[2rem] border border-border bg-card/40 p-10 lg:p-14">
          <h2 className="font-display text-2xl md:text-3xl">
            Browse other <span className="text-gradient">categories</span>
          </h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Every article on the field journal is filed into one of these tracks.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {otherCategories.map(([s, e]) => (
              <li key={s}>
                <Link
                  to="/blog/category/$category"
                  params={{ category: s }}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                >
                  {e.name}
                  <span className="text-[10px] tabular-nums opacity-60">{e.posts.length}</span>
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs uppercase tracking-[0.16em] hover:opacity-90 transition"
              >
                All articles
                <ArrowUpRight size={12} />
              </Link>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}